import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { convertGraphqlBaseTestItemToDto, convertGraphqlFormFieldToDto, convertGraphqlResponseToDto, convertGraphqlResponseToDtoSimpleReturnValue } from 'src/app/graphQL/mappers/common.mappers';
import { GraphQLBaseTestItemOutput } from 'src/app/graphQL/models/output/graphql-base-test-item-output';
import { GraphQLFormFieldOutput } from 'src/app/graphQL/models/output/graphql-form-field-output';
import { GraphQLResponseOutput } from 'src/app/graphQL/models/output/graphql-response-output';
import { BaseTestItemDto } from 'src/app/models/dto/Base-Test-item-dto';
import { ResponseDto } from 'src/app/models/dto/responseDto';
import { formField } from 'src/app/models/formField';
import { ApolloClientService } from './apollo-client.service';
import { GET_SW_COVERAGE_QUERY, GET_SW_COVERAGE_SEARCH_UI_TEMPLATE_QUERY, GET_SW_COVERAGE_UI_TEMPLATE_QUERY, GETALL_SW_COVERAGE_QUERY, SEARCH_SW_COVERAGE_QUERY } from 'src/app/graphQL/queries/software-version-coverage-queries';
import { SoftwareVersionCoverageDto } from 'src/app/models/dto/softwareVersionCoverageDto';
import { convertSoftwareVersionCoverageDtoToGraphqlInput } from 'src/app/graphQL/mappers/software-version-coverage-mappers';
import { CREATE_SW_COVERAGE_MUTATION, DELETE_SW_COVERAGE_MUTATION, UPDATE_SW_COVERAGE_MUTATION } from 'src/app/graphQL/mutations/software-version-coverage-mutations';
import { DualApiService, Scope } from '../migration/dual-api.service';

@Injectable({
  providedIn: 'root'
})
export class SoftwareVersionCoverageApiService {

  constructor(
    private apolloClientService: ApolloClientService,
    private dualApiService: DualApiService
  ) {}

  /**
   * `regBy` is the old API's "only mine" switch, which maps onto the Go lambda's
   * `scope: MINE` (resolved server-side from the authorizer, so the client cannot ask for
   * someone else's items). Nothing maps to MY_GROUP: no software version coverage item
   * carries functionalGroupOwners, so that scope would always return an empty page.
   */
  getAll(regBy: string): Observable<BaseTestItemDto[]> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorAllSoftwareVersionCoverages : GraphQLBaseTestItemOutput[]}>({
          query: GETALL_SW_COVERAGE_QUERY,
          variables: {RegBy: regBy}
        })
      ),
      map(results => {
        const swCoverages = results.data.configuratorAllSoftwareVersionCoverages
        if (!swCoverages)
          return undefined

        return swCoverages.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    const scope: Scope | undefined = regBy ? 'MINE' : undefined;

    return this.dualApiService.dualGetAll('softwareVersionCoverage', [], oldApiCall$, scope);
  }

  getByUid(uid: string): Observable<ResponseDto> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSoftwareVersionCoverageById: GraphQLResponseOutput}>({
          query: GET_SW_COVERAGE_QUERY,
          variables: {Uid: uid}
        })
      ),
      map(results => {
        const swCoverage = results.data.configuratorSoftwareVersionCoverageById
        if (!swCoverage)
          return undefined

        return convertGraphqlResponseToDto<SoftwareVersionCoverageDto>(swCoverage);
      })
    );

    return this.dualApiService.dualGetByUid('softwareVersionCoverage', uid, oldApiCall$);
  }

  // UI templates stay on the old API, as for every other entity.
  getUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSoftwareVersionCoverageUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_SW_COVERAGE_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const swCoverageUiTemplate = results.data.configuratorSoftwareVersionCoverageUiTemplate
        if (!swCoverageUiTemplate)
          return undefined

        return swCoverageUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  getSearchUiTemplate(): Observable<formField[]> {
    return this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSoftwareVersionCoverageSearchUiTemplate: GraphQLFormFieldOutput[]}>({
          query: GET_SW_COVERAGE_SEARCH_UI_TEMPLATE_QUERY,
          variables: {}
        })
      ),
      map(results => {
        const swCoverageUiTemplate = results.data.configuratorSoftwareVersionCoverageSearchUiTemplate

        if (!swCoverageUiTemplate)
          return undefined

        return swCoverageUiTemplate.map(item => {
          return convertGraphqlFormFieldToDto(item);
        })
      })
    )
  }

  create(swCoverage: SoftwareVersionCoverageDto): Observable<ResponseDto> {
    const graphqlSwCoverageInput = convertSoftwareVersionCoverageDtoToGraphqlInput(swCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorCreateSoftwareVersionCoverage: GraphQLResponseOutput}>({
          mutation: CREATE_SW_COVERAGE_MUTATION,
          variables: {SoftwareVersionCoverage: graphqlSwCoverageInput }
        })
      ),
      map(result => {
        const configuratorCreatedSwCoverage = result.data.configuratorCreateSoftwareVersionCoverage
        if (!configuratorCreatedSwCoverage)
          return undefined
        return convertGraphqlResponseToDtoSimpleReturnValue(configuratorCreatedSwCoverage as GraphQLResponseOutput)
      })
    );

    // The DTO, not the PascalCase GraphQL input: GoLambdaMapperService does its own
    // camelCase conversion and Go-schema cleaning — including wrapping the single ECU
    // string into the ecuIds array the Go schema requires.
    return this.dualApiService.dualCreate('softwareVersionCoverage', swCoverage, oldApiCall$);
  }

  update(swCoverage: SoftwareVersionCoverageDto): Observable<ResponseDto> {
    const graphqlSwCoverageInput = convertSoftwareVersionCoverageDtoToGraphqlInput(swCoverage)

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate<{configuratorUpdateSoftwareVersionCoverage: GraphQLResponseOutput}>({
          mutation: UPDATE_SW_COVERAGE_MUTATION,
          variables: {SoftwareVersionCoverage: graphqlSwCoverageInput }
        })
      ),
      map(result =>{
        const configuratorUpdatedSwCoverage = result.data.configuratorUpdateSoftwareVersionCoverage
        if (!configuratorUpdatedSwCoverage)
          return undefined
        return convertGraphqlResponseToDtoSimpleReturnValue(configuratorUpdatedSwCoverage as GraphQLResponseOutput)
      })
    );

    // dualUpdate strips uid out of the payload — the Go mutation takes it as its own
    // argument and rejects it inside data.
    return this.dualApiService.dualUpdate('softwareVersionCoverage', swCoverage.uid, swCoverage, oldApiCall$);
  }

  delete(uid: string): Observable<any> {
    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DELETE_SW_COVERAGE_MUTATION,
          variables: {Uid: uid}
        })
      ),
      map(result =>(result.data))
    );

    return this.dualApiService.dualDelete('softwareVersionCoverage', uid, oldApiCall$);
  }

  search(swCoverage: SoftwareVersionCoverageDto, scope?: Scope): Observable<BaseTestItemDto[]> {
    const graphqlSwCoverageInput = convertSoftwareVersionCoverageDtoToGraphqlInput(swCoverage);

    const oldApiCall$ = this.apolloClientService.apolloClient$.pipe(
      switchMap(client =>
        client.query<{configuratorSearchSoftwareVersionCoverage: GraphQLBaseTestItemOutput[]}>({
          query: SEARCH_SW_COVERAGE_QUERY,
          variables: {SoftwareVersionCoverage: graphqlSwCoverageInput }
        })
      ),
      map(results => {
        const swCoverages = results?.data?.configuratorSearchSoftwareVersionCoverage
        if (!swCoverages)
          return undefined;

        return swCoverages.map(item => {
          return convertGraphqlBaseTestItemToDto(item);
        })
      })
    );

    return this.dualApiService.dualSearch('softwareVersionCoverage', swCoverage, oldApiCall$, scope);
  }
}
